import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAdminSetup } from '../../hooks/useAdminSetup';
import TableIcon from '../../components/TableIcon';
import CurrentPlanCard from '../../components/CurrentPlanCard';
// import DebugInfo from '../../components/DebugInfo';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { dashboardService } from '../../services/dashboard';
import { useTutorialRefs } from '../../context/TutorialRefsContext';
import { recommendationsService } from '../../services/recommendations';

const quickActions = [
  {
    title: 'Restaurantes',
    icon: '🏨',
    route: '/admin/restaurants',
    gradient: ['#F3F4F6', '#E5E7EB'],
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Tu Menú',
    subtitle: 'Gestionar categorías y platos',
    icon: '🍽️',
    route: '/admin/menu',
    gradient: ['#F8FAFC', '#E2E8F0'],
    description: 'Crea y edita tu menú completo',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Responsables',
    subtitle: 'Administrar gerentes y cocina',
    icon: '👥',
    route: '/admin/responsibles',
    gradient: ['#E0E7FF', '#C7D2FE'],
    description: 'Coordina roles y responsabilidades',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Meseros',
    subtitle: 'Administrar personal y asignaciones',
    icon: '/assets/images/mesero.png',
    route: '/admin/waiters',
    gradient: ['#DBEAFE', '#BFDBFE'],
    description: 'Gestiona tu equipo de servicio',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Mesas',
    subtitle: 'Crear, editar y configurar mesas',
    icon: <TableIcon size={48} />,
    route: '/admin/tables',
    gradient: ['#F1F5F9', '#CBD5E1'],
    description: 'Organiza el layout de tu restaurante',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Reservaciones',
    subtitle: 'Crear, editar y configurar reservaciones',
    icon: '🔖',
    route: '/admin/reservations',
    gradient: ['#F1F5F9', '#CBD5E1'],
    description: 'Organiza el layout de tu restaurante',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Cocina',
    subtitle: 'Gestionar órdenes y preparación',
    icon: '👨‍🍳',
    route: '/admin/kitchen',
    gradient: ['#FEF3C7', '#FDE68A'],
    description: 'Controla el flujo de la cocina',
    comingSoon: false,
    allowedRoles: ['owner', 'manager', 'kitchen'],
  },
  {
    title: 'Reportes de Ventas',
    subtitle: 'Análisis detallado de ingresos',
    icon: '📈',
    route: '/admin/sales',
    gradient: ['#D1FAE5', '#A7F3D0'],
    description: 'Analiza el rendimiento financiero',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Inteligencia Artificial (IA)',
    subtitle: 'Consejos y recomendaciones',
    icon: '🤖',
    route: '/admin/analytics',
    gradient: ['#FCE7F3', '#FBCFE8'],
    description: 'Métricas avanzadas y insights',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Planes y Precios',
    subtitle: 'Gestionar suscripción y planes',
    icon: '💰',
    route: '/pricing',
    gradient: ['#FEF3C7', '#FDE68A'],
    description: 'Ver y cambiar tu plan de suscripción',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
  {
    title: 'Promociones',
    icon: '🎉',
    route: '/admin/promotions',
    gradient: ['#FEF3C7', '#FDE68A'],
    description: 'Gestiona tus promociones y ofertas especiales.',
    comingSoon: false,
    allowedRoles: ['owner', 'manager'],
  },
];

// Pasos del tutorial específicos para cada acción rápida y header
const tutorialSteps = [
  {
    key: 'menu-hamburguesa',
    title: '☰ Menú Rápido',
    description: 'En la parte superior izquierda verás el botón ☰. Este menú estará disponible en todas las vistas y te da acceso rápido a las acciones principales del sistema.',
  },
  {
    key: 'selector-restaurante',
    title: 'Selector de Restaurante',
    description: 'En el centro del header encontrarás el selector de restaurantes. Cambia entre los restaurantes que administras para gestionar su información (menú, mesas, reservaciones, etc.).',
  },
  {
    key: 'abrir-cerrar-restaurante',
    title: 'Abrir/Cerrar Restaurante',
    description: 'En el selector de restaurantes, puedes abrir o cerrar el restaurante rápidamente. También puedes hacerlo desde la sección de Restaurantes.',
  },
  // Acciones rápidas
  {
    key: 'Restaurantes',
    title: 'Restaurantes',
    description: 'Lo primero. Crea tus restaurante(s) y controla su estado (Abrir/Cerrar) diariamente. Si NO abres, los clientes no podrán hacer pedidos; si no cierras, las órdenes no se detendrán.',
  },
  {
    key: 'Tu Menú',
    title: 'Tu Menú',
    description: 'Crea y edita tus platillos y categorías. Los clientes pueden ver el menú en solo lectura desde "Ver menú" en historial (cuando se navega con restaurantId sin mesa no se permite pedir).',
  },
  {
    key: 'Responsables',
    title: 'Responsables',
    description: 'Asigna y gestiona los responsables (gerentes y cocineros) de cada restaurante. El agregar gerente es opcional si tú administras tu restaurante.',
  },
  {
    key: 'Meseros',
    title: 'Meseros',
    description: 'Agrega meseros con correo Gmail de cada uno. Cuando ellos inicien sesión en el sistema podrán ver las órdenes según les correspondan.',
  },
  {
    key: 'Mesas',
    title: 'Mesas',
    description: 'Agrega mesas, genera códigos QR y asigna meseros. En "Layout de Mesas" puedes arrastrar y colocar mesas por área, hacer foco en un área para acomodar mejor y guardar manualmente (no se guarda automáticamente al arrastrar).',
  },
  {
    key: 'Reservaciones',
    title: 'Reservaciones',
    description: 'Gestiona reservaciones. El layout refleja ocupación: en el cliente, "Ver mesas / Reservar" muestra el plano en solo lectura con mesas ocupadas/libres.',
  },
  {
    key: 'Cocina',
    title: 'Cocina',
    description: 'Visualiza y gestiona las órdenes en tiempo real.',
  },
  {
    key: 'Reportes de Ventas',
    title: 'Reportes de Ventas',
    description: 'Consulta estadísticas y ventas (próximamente).',
  },
  {
    key: 'Análisis y Métricas',
    title: 'Análisis y Métricas',
    description: 'Revisa métricas avanzadas del negocio (próximamente).',
  },
  {
    key: 'Planes y Precios',
    title: 'Planes y Precios',
    description: 'Gestiona tu suscripción y cambia entre planes según tus necesidades.',
  },
  {
    key: 'Promociones',
    title: 'Promociones',
    description: 'Gestiona tus promociones y ofertas; aparecerán en el menú digital.',
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedRestaurantName, selectedRestaurantId, selectedRestaurantLogo } = useSelectedRestaurant();
  const [statsLoading, setStatsLoading] = useState(false);
  const [todayOrders, setTodayOrders] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [tipsToday, setTipsToday] = useState<Array<{
    id: string;
    title: string;
    description: string;
    icon: string;
    priority: 'alto' | 'medio' | 'bajo' | 'desconocido';
    why?: string;
    actions?: string[];
  }>>([]);
  const [tipsOpen, setTipsOpen] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.uid || !selectedRestaurantId) return;
      try {
        setStatsLoading(true);
        const stats = await dashboardService.getDashboardStats(user.uid, selectedRestaurantId);
        setTodayOrders(stats.todayOrders || 0);
        setTodaySales(stats.todaySales || 0);
        // dashboardService ya persistió el timezone si vino en respuesta.
      } catch (e) {
        console.error('Error cargando estadísticas del dashboard', e);
        setTodayOrders(0);
        setTodaySales(0);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [user?.uid, selectedRestaurantId]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(value || 0);

  // Cargar Consejos de IA del día (hoy)
  useEffect(() => {
    const loadTips = async () => {
      if (!selectedRestaurantId) return;
      setTipsLoading(true);
      try {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const record = await recommendationsService.getByRestaurant(selectedRestaurantId, { date: `${yyyy}-${mm}-${dd}` });
        const tips: Array<{
          id: string;
          title: string;
          description: string;
          icon: string;
          priority: 'alto' | 'medio' | 'bajo' | 'desconocido';
          why?: string;
          actions?: string[];
        }> = [];
        if (record && Array.isArray(record.recommendations) && record.recommendations.length > 0) {
          const first = record.recommendations[0];
          const rawText: string = String(first?.text || '');
          const lines = rawText.split('\n').map(s => s.trim()).filter(Boolean);
          const items = lines.filter(l => /^\d+\)/.test(l)).slice(0, 4);
          if (items.length > 0) {
            items.forEach((l, i) => {
              const content = l.replace(/^\d+\)\s*/, '');
              const title = content.split('—')[0]?.trim() || `Recomendación #${i + 1}`;
              const icon = i === 0 ? '🤖' : i === 1 ? '💡' : i === 2 ? '📉' : '⭐';
              const lower = content.toLowerCase();
              const prioMatch = lower.match(/prioridad:\s*(alto|medio|bajo)/);
              const priority = (prioMatch?.[1] as 'alto' | 'medio' | 'bajo' | undefined) || 'desconocido';
              const whyMatch = content.match(/por qué:\s*([^\.]+(?:\.[^a].*)?)/i);
              const actionsMatch = content.match(/acciones:\s*(.+?)(?:\.|\s*evidencia:|$)/i);
              const actions = actionsMatch ? actionsMatch[1].split(';').map(s => s.trim()).filter(Boolean) : [];
              tips.push({ id: `tip-${i}`, title, description: content, icon, priority, why: whyMatch ? whyMatch[1].trim() : undefined, actions });
            });
          } else if (rawText) {
            tips.push({ id: 'tip-0', title: 'Recomendaciones generadas', description: rawText.slice(0, 240), icon: '🤖', priority: 'desconocido' });
          }
        }
        setTipsToday(tips);
      } catch (err) {
        setTipsToday([]);
      } finally {
        setTipsLoading(false);
      }
    };
    loadTips();
  }, [selectedRestaurantId]);

  const { 
    needsOnboarding,
    isLoading: setupLoading, 
    loadAdminSetup,
    error,
    shouldShowTutorial,
    markTutorialAsShown,
  } = useAdminSetup();

  const [helpHover, setHelpHover] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, position: 'top', arrowOffset: 0 });
  const hasAutoStartedTutorial = useRef(false);
  const hasAutoScrolledForStepRef = useRef(false);
  const autoScrollTimerRef = useRef<number | null>(null);
  const programmaticScrollRef = useRef(false);
  const programmaticScrollEndTimerRef = useRef<number | null>(null);
  // Siempre refleja el paso ACTUAL (se actualiza en useEffect)
  const activeStepKeyRef = useRef<string>('');
  const positionRetryTimerRef = useRef<number | null>(null);
  const lateRecalcTimerRef = useRef<number | null>(null);
  const visibilityWatchdogRef = useRef<number | null>(null);
  const rafScheduledRef = useRef(false);
  const autoScrollAttemptsRef = useRef<Record<string, number>>({});
  const prevTooltipRef = useRef<{ top: number; left: number; position: string; arrowOffset: number; visible: boolean } | null>(null);
  const tooltipVisibleRef = useRef(false);
  const lastCalcAtRef = useRef<number>(0);
  const prevScrollYRef = useRef<number>(0);
  const positioningLockRef = useRef(false);
  const watchdogAttemptsRef = useRef<Record<string, number>>({});
  const tutorialActiveRef = useRef(false);

  // Rol actual simplificado
  const roleId = (user?.role?.id || '').toLowerCase();
  const isOwnerManager = roleId === 'owner' || roleId === 'manager';

  // Obtener refs del contexto de tutorial
  const { menuButtonRef, restaurantSelectorRef } = useTutorialRefs();
  
  // Refs para cada acción rápida (mapeadas por título para evitar desalineación al filtrar)
  const quickActionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  useEffect(() => {
    if (needsOnboarding) {
      loadAdminSetup();
    }
  }, [needsOnboarding, loadAdminSetup]);

  // Log del estado inicial y reset de hasAutoStartedTutorial
  useEffect(() => {
    console.log('🏗️ Dashboard montado - Estado inicial:', {
      shouldShowTutorial,
      tutorialActive,
      setupLoading,
      needsOnboarding,
      hasAutoStarted: hasAutoStartedTutorial.current
    });
    
    // Resetear hasAutoStartedTutorial si shouldShowTutorial cambia a false
    if (!shouldShowTutorial && hasAutoStartedTutorial.current) {
      console.log('🔄 Reseteando hasAutoStartedTutorial porque shouldShowTutorial es false');
      hasAutoStartedTutorial.current = false;
    }
  }, [shouldShowTutorial, tutorialActive, setupLoading, needsOnboarding]);

  // Efecto para mostrar tutorial automáticamente la primera vez
  useEffect(() => {
    console.log('🔍 useEffect tutorial - Estado actual:', {
      shouldShowTutorial,
      tutorialActive,
      setupLoading,
      hasAutoStarted: hasAutoStartedTutorial.current,
      shouldExecute: shouldShowTutorial && !tutorialActive && !setupLoading && !hasAutoStartedTutorial.current
    });
    
    if (shouldShowTutorial && !tutorialActive && !setupLoading && !hasAutoStartedTutorial.current) {
      console.log('🎯 Mostrando tutorial automáticamente (primera vez)');
      hasAutoStartedTutorial.current = true; // Marcar que ya se ejecutó
      
      // Pequeño delay para asegurar que todo esté renderizado
      const timer = setTimeout(() => {
        console.log('⏰ Timer ejecutado - Llamando startTutorial');
        startTutorial();
      }, 1000);
      
      return () => {
        console.log('🧹 Limpiando timer del tutorial automático');
        clearTimeout(timer);
      };
    }
  }, [shouldShowTutorial, tutorialActive, setupLoading]);

  // Función para actualizar posición del tooltip
  const updateTooltipPosition = () => {
    const now = Date.now();
    // Throttle duro para evitar bucles (mínimo 90ms entre cálculos)
    if (now - lastCalcAtRef.current < 90) return;
    lastCalcAtRef.current = now;
    // Si estamos bloqueados y el tooltip ya es visible, evitar recalcular para no entrar en loop
    if (positioningLockRef.current && (prevTooltipRef.current?.visible || tooltipVisibleRef.current)) return;
    console.log('📍 updateTooltipPosition llamado, tutorialActive:', tutorialActive);
    if (!tutorialActiveRef.current) return;

    const step = tutorialSteps[tutorialStep];
    const stepKey = step.key;
    console.log('📋 Paso actual:', step);
    let ref: HTMLElement | null = null;

    if (step.key === 'menu-hamburguesa') {
      ref = menuButtonRef.current;
      console.log('🍔 Ref del menú hamburguesa:', ref);
    } else if (step.key === 'selector-restaurante') {
      ref = restaurantSelectorRef.current;
      console.log('🏪 Ref del selector de restaurante:', ref);
    } else if (step.key === 'abrir-cerrar-restaurante') {
      // Ahora el abrir/cerrar se controla desde el selector de restaurante
      ref = restaurantSelectorRef.current;
      console.log('🚪 Ref (abrir/cerrar) anclado al selector de restaurante:', ref);
    } else {
      // Acciones rápidas: obtener ref por título (robusto ante filtros/orden)
      ref = quickActionRefs.current[step.title] || null;
      console.log('🎯 Ref de acción rápida por título:', step.title, ref);
    }

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const isMobile = windowWidth < 768;
    const tooltipWidth = isMobile ? Math.min(280, windowWidth - 40) : 320;
    const tooltipHeight = isMobile ? 140 : 120;
    const gap = 12; // separación visual entre tooltip y elemento
    const isHeaderStepQuick = (step.key === 'menu-hamburguesa' || step.key === 'selector-restaurante' || step.key === 'abrir-cerrar-restaurante');

    // En mobile para pasos del header, render inmediato como bottom sheet fijo (sin depender de refs)
    if (isMobile && isHeaderStepQuick) {
      // Intentar ANCLAR al elemento del header; si no cabe, fallback a bottom sheet
      let anchorEl: HTMLElement | null = null;
      if (step.key === 'menu-hamburguesa') anchorEl = menuButtonRef.current;
      if (step.key === 'selector-restaurante' || step.key === 'abrir-cerrar-restaurante') anchorEl = restaurantSelectorRef.current;
      if (anchorEl && anchorEl.getBoundingClientRect) {
        const r = anchorEl.getBoundingClientRect();
        let anchorX = r.left + r.width / 2;
        let left = Math.max(10, Math.min(anchorX - tooltipWidth / 2, windowWidth - tooltipWidth - 10));
        const spaceAbove = r.top;
        const spaceBelow = windowHeight - r.bottom;
        const fitsBelow = spaceBelow >= (tooltipHeight + gap + 20);
        const fitsAbove = spaceAbove >= (tooltipHeight + gap + 20);
        let top: number;
        let position: 'top' | 'bottom' | 'center' = 'bottom';
        if (fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove)) {
          top = r.bottom + gap;
          position = 'bottom';
        } else if (fitsAbove) {
          top = r.top - tooltipHeight - gap;
          position = 'top';
        } else {
          // Fallback a bottom sheet fijo
          top = Math.max(10, windowHeight - tooltipHeight - 20);
          left = Math.max(10, (windowWidth - tooltipWidth) / 2);
          position = 'bottom';
        }
        const arrowOffset = Math.max(8, Math.min(tooltipWidth - 8, anchorX - left));
        if (activeStepKeyRef.current !== step.key) return;
        setTooltipPosition({ top, left, position, arrowOffset });
        setTooltipVisible(true);
        prevTooltipRef.current = { top, left, position, arrowOffset, visible: true };
        return;
      } else {
        // Si no hay referencia (raro), usar bottom sheet centrado como última opción
        const left = Math.max(10, (windowWidth - tooltipWidth) / 2);
        const top = Math.max(10, windowHeight - tooltipHeight - 20);
        const arrowOffset = tooltipWidth / 2;
        if (activeStepKeyRef.current !== step.key) return;
        setTooltipPosition({ top, left, position: 'bottom', arrowOffset });
        setTooltipVisible(true);
        prevTooltipRef.current = { top, left, position: 'bottom', arrowOffset, visible: true };
        return;
      }
    }

    console.log('📱 Dimensiones:', { windowWidth, windowHeight, isMobile, tooltipWidth, tooltipHeight });

    if (ref && ref.getBoundingClientRect) {
      const rect = ref.getBoundingClientRect();
      console.log('📐 Rectángulo del elemento:', rect);
      // Si el elemento aún no tiene tamaño visible (p.ej. render tardío), reintentar
      if ((rect.width === 0 && rect.height === 0) || (rect.top === 0 && rect.bottom === 0)) {
        if (positionRetryTimerRef.current) {
          window.clearTimeout(positionRetryTimerRef.current);
        }
        setTooltipVisible(false);
        const expectedKey = stepKey;
        positionRetryTimerRef.current = window.setTimeout(() => {
          if (tutorialActiveRef.current && activeStepKeyRef.current === expectedKey) updateTooltipPosition();
        }, 120);
        return;
      }
      
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      let top = rect.top - tooltipHeight - gap;
      let position: 'top' | 'bottom' | 'center' = 'top';
      // Punto de anclaje horizontal al centro del elemento
      const anchorX = rect.left + (rect.width / 2);

      // Ajustes específicos para móviles
      if (isMobile) {
        // En mobile, anclar horizontalmente al centro del elemento (no centrar por pantalla)
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        // Clamp horizontal dentro de pantalla
        left = Math.max(10, Math.min(left, windowWidth - tooltipWidth - 10));
        // Decidir arriba/abajo por espacio disponible real
        const spaceAbove = rect.top;
        const spaceBelow = windowHeight - rect.bottom;
        const needsBelow = spaceAbove < (gap + 60) && spaceBelow > spaceAbove; // muy poco espacio arriba
        const fitsBelow = spaceBelow >= (tooltipHeight + gap + 20);
        const fitsAbove = spaceAbove >= (tooltipHeight + gap + 20);
        if (fitsAbove && !needsBelow) {
          // Mantener arriba (ya está calculado por defecto)
          top = rect.top - tooltipHeight - gap;
          position = 'top';
        } else if (fitsBelow || needsBelow) {
          top = rect.bottom + gap;
          position = 'bottom';
        } else {
          // Si no cabe cómodo, centrar
          top = Math.max(100, (windowHeight - tooltipHeight) / 2);
          position = 'center';
        }
      }

      // Asegurar que el tooltip no se salga de la pantalla horizontalmente
      if (left < 20) left = 20;
      if (left + tooltipWidth > windowWidth - 20) {
        left = windowWidth - tooltipWidth - 20;
      }

      // Asegurar que el tooltip no se salga de la pantalla verticalmente
      if (top < 80) {
        // Si no cabe arriba, poner abajo del elemento
        top = rect.bottom + gap;
        position = 'bottom';
      }

      // Si tampoco cabe abajo, centrar en la pantalla
      if (top + tooltipHeight > windowHeight - 100) {
        top = Math.max(100, (windowHeight - tooltipHeight) / 2);
        position = 'center';
      }

      // Ajustar por visualViewport en móviles (barra/zoom pueden desplazar el viewport visual)
      const vvo = (window as any).visualViewport;
      if (vvo) {
        left += Number(vvo.offsetLeft || 0);
        top += Number(vvo.offsetTop || 0);
      }
      // Clamp final dentro de viewport
      left = Math.max(10, Math.min(left, (window.innerWidth - tooltipWidth - 10)));
      top = Math.max(10, Math.min(top, (window.innerHeight - tooltipHeight - 10)));

      // Para elementos que están fuera de la vista, mostrar tooltip centrado con piquito
      const farOutOfView = rect.bottom < 0 || rect.top > windowHeight;
      if (farOutOfView) {
        console.log('⚠️ Elemento fuera de vista, mostrando tooltip con piquito');
        // Calcular posición del tooltip para que apunte al elemento
        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        let top = 100; // Posición fija cerca del top
        let position = 'bottom'; // Piquito apunta hacia arriba
        
        // Asegurar que el tooltip no se salga de la pantalla horizontalmente
        if (left < 20) left = 20;
        if (left + tooltipWidth > windowWidth - 20) {
          left = windowWidth - tooltipWidth - 20;
        }
        const vvo2 = (window as any).visualViewport;
        if (vvo2) {
          left += Number(vvo2.offsetLeft || 0);
          top += Number(vvo2.offsetTop || 0);
        }
        // Recalcular offset del piquito para apuntar al centro del elemento
        const anchorX2 = rect.left + (rect.width / 2);
        const arrowOffset = Math.max(8, Math.min(tooltipWidth - 8, anchorX2 - left));
        if (activeStepKeyRef.current !== step.key) return;
        setTooltipPosition({ top, left, position, arrowOffset });
        setTooltipVisible(true);
        return;
      }

      console.log('✅ Posición calculada:', { top, left, position });
      // Calcular offset del piquito según ancla
      const arrowAnchorX = anchorX;
      const arrowOffset = Math.max(8, Math.min(tooltipWidth - 8, arrowAnchorX - left));
      // Ignorar si el paso cambió mientras se calculaba
      if (activeStepKeyRef.current !== stepKey) return;
      // Evitar re-renders si no hay cambios significativos
      const prev = prevTooltipRef.current;
      const changed = !prev || Math.abs(prev.top - top) > 1 || Math.abs(prev.left - left) > 1 || prev.position !== position || Math.abs(prev.arrowOffset - arrowOffset) > 1 || !prev.visible;
      if (changed) {
        setTooltipPosition({ top, left, position, arrowOffset });
        setTooltipVisible(true);
        prevTooltipRef.current = { top, left, position, arrowOffset, visible: true };
        // Si hay watchdog activo, cancelar cuando ya es visible
        if (visibilityWatchdogRef.current) {
          window.clearTimeout(visibilityWatchdogRef.current);
          visibilityWatchdogRef.current = null;
        }
        // Bloquear recalculados por un breve lapso para evitar re-loops
        positioningLockRef.current = true;
        window.setTimeout(() => { positioningLockRef.current = false; }, 400);
      }
      return;
    } else {
      // Si no se encuentra la referencia, mostrar el tooltip centrado
      console.log(`❌ No se encontró referencia para: ${step.key}, mostrando tooltip centrado`);
      const centerTop = (windowHeight - tooltipHeight) / 2;
      const centerLeft = (windowWidth - tooltipWidth) / 2;
      console.log('🎯 Posición centrada:', { top: centerTop, left: centerLeft, position: 'center' });
      // En lugar de fijarlo centrado inmediatamente (puede causar salto al paso anterior),
      // ocultamos y reintentamos breve hasta que exista la ref real del paso actual
      if (positionRetryTimerRef.current) {
        window.clearTimeout(positionRetryTimerRef.current);
      }
      setTooltipVisible(false);
      const expectedKey = stepKey;
      positionRetryTimerRef.current = window.setTimeout(() => {
        if (tutorialActiveRef.current && activeStepKeyRef.current === expectedKey) updateTooltipPosition();
      }, 120);
    }
  };

  // Actualizar posición cuando cambie el paso del tutorial
  useEffect(() => {
    if (tutorialActive) {
      // Actualizar referencia del paso actual para validaciones anti-race
      activeStepKeyRef.current = tutorialSteps[tutorialStep]?.key || '';
      // Reiniciar intentos de auto-scroll para este paso
      const k = activeStepKeyRef.current;
      if (k) autoScrollAttemptsRef.current[k] = 0;
      if (k) watchdogAttemptsRef.current[k] = 0;
      hasAutoScrolledForStepRef.current = false;
      // Cancelar timers en transición de paso para evitar recálculos del paso anterior
      if (autoScrollTimerRef.current) {
        window.clearTimeout(autoScrollTimerRef.current);
        autoScrollTimerRef.current = null;
      }
      if (programmaticScrollEndTimerRef.current) {
        window.clearTimeout(programmaticScrollEndTimerRef.current);
        programmaticScrollEndTimerRef.current = null;
      }
      if (positionRetryTimerRef.current) {
        window.clearTimeout(positionRetryTimerRef.current);
        positionRetryTimerRef.current = null;
      }
      if (lateRecalcTimerRef.current) {
        window.clearTimeout(lateRecalcTimerRef.current);
        lateRecalcTimerRef.current = null;
      }
      if (visibilityWatchdogRef.current) {
        window.clearTimeout(visibilityWatchdogRef.current);
        visibilityWatchdogRef.current = null;
      }
      positioningLockRef.current = false;
      setTooltipVisible(false);
      const isMobile = window.innerWidth < 768;
      // Dar un poco más de tiempo en mobile para montar y medir
      setTimeout(updateTooltipPosition, isMobile ? 250 : 100);

      // Watchdog: si no aparece, forzar un nuevo cálculo y finalmente un fallback centrado
      const isHeaderStep = (activeStepKeyRef.current === 'menu-hamburguesa' || activeStepKeyRef.current === 'selector-restaurante' || activeStepKeyRef.current === 'abrir-cerrar-restaurante');
      if (!isHeaderStep) {
        visibilityWatchdogRef.current = window.setTimeout(() => {
          const key = activeStepKeyRef.current;
          const attempts = watchdogAttemptsRef.current[key] || 0;
          if (tutorialActiveRef.current && !tooltipVisibleRef.current && (key === (tutorialSteps[tutorialStep]?.key || '')) && attempts < 2) {
            watchdogAttemptsRef.current[key] = attempts + 1;
            updateTooltipPosition();
            // Segundo intento: forzar centrado si aún no es visible
            window.setTimeout(() => {
              if (tutorialActiveRef.current && !tooltipVisibleRef.current && (key === (tutorialSteps[tutorialStep]?.key || ''))) {
                const ww = window.innerWidth; const wh = window.innerHeight;
                const tw = ww < 768 ? Math.min(280, ww - 40) : 320;
                const th = ww < 768 ? 140 : 120;
                setTooltipPosition({ top: Math.max(80, (wh - th) / 2), left: Math.max(20, (ww - tw) / 2), position: 'center', arrowOffset: tw / 2 });
                setTooltipVisible(true);
                prevTooltipRef.current = { top: Math.max(80, (wh - th) / 2), left: Math.max(20, (ww - tw) / 2), position: 'center', arrowOffset: tw / 2, visible: true };
                positioningLockRef.current = true;
                window.setTimeout(() => { positioningLockRef.current = false; }, 300);
              }
            }, 250);
          }
        }, isMobile ? 600 : 400);
      }
    }
  }, [tutorialStep, tutorialActive]);

  // Actualizar posición durante el scroll
  useEffect(() => {
    const handleScroll = () => {
      if (tutorialActive) {
        // Ignorar eventos de scroll disparados por auto-scroll para evitar rebotes
        if (programmaticScrollRef.current) return;
        // Evitar recalcular si el scroll cambió muy poco
        const y = window.scrollY;
        if (Math.abs(y - prevScrollYRef.current) < 3) return;
        prevScrollYRef.current = y;
        // En mobile, permitir seguir a la feature excepto en pasos del header (para evitar loops con header sticky)
        if (window.innerWidth < 768) {
          const k = tutorialSteps[tutorialStep]?.key;
          const isHeaderStep = k === 'menu-hamburguesa' || k === 'selector-restaurante' || k === 'abrir-cerrar-restaurante';
          if (isHeaderStep) return;
        }
        if (rafScheduledRef.current) return;
        rafScheduledRef.current = true;
        window.requestAnimationFrame(() => {
          rafScheduledRef.current = false;
          updateTooltipPosition();
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [tutorialActive, tutorialStep]);

  const handleTutorialNext = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTooltipVisible(false);
      setTimeout(() => {
        hasAutoScrolledForStepRef.current = false;
        if (autoScrollTimerRef.current) { window.clearTimeout(autoScrollTimerRef.current); autoScrollTimerRef.current = null; }
        if (programmaticScrollEndTimerRef.current) { window.clearTimeout(programmaticScrollEndTimerRef.current); programmaticScrollEndTimerRef.current = null; }
        if (positionRetryTimerRef.current) { window.clearTimeout(positionRetryTimerRef.current); positionRetryTimerRef.current = null; }
        if (lateRecalcTimerRef.current) { window.clearTimeout(lateRecalcTimerRef.current); lateRecalcTimerRef.current = null; }
        // Sincronizar inmediatamente el stepKey esperado para evitar flasheos del paso anterior
        const nextIndex = tutorialStep + 1;
        const nextKey = tutorialSteps[nextIndex]?.key || '';
        activeStepKeyRef.current = nextKey;
        setTutorialStep(nextIndex);
        // Recalcular tras breve delay
        setTimeout(() => {
          updateTooltipPosition();
          // Retry acotado si aún no es visible (referencias tardías en mobile)
          let tries = 0;
          const maxTries = 3;
          const interval = setInterval(() => {
            if (tooltipVisibleRef.current || tries >= maxTries || !tutorialActiveRef.current) {
              clearInterval(interval);
              return;
            }
            tries++;
            updateTooltipPosition();
          }, 180);
        }, 200);
      }, 200);
    } else {
      // Tutorial completado
      console.log('✅ Tutorial completado completamente');
      markTutorialAsShown();
      setTutorialActive(false);
      console.log('🔄 Estado después de completar:', { tutorialActive: false, shouldShowTutorial: false });
    }
  };

  const handleTutorialClose = () => {
    // Tutorial cerrado manualmente
    console.log('🚫 Tutorial cerrado manualmente');
    markTutorialAsShown();
    setTutorialActive(false);
    hasAutoScrolledForStepRef.current = false;
    programmaticScrollRef.current = false;
    if (autoScrollTimerRef.current) {
      window.clearTimeout(autoScrollTimerRef.current);
      autoScrollTimerRef.current = null;
    }
    if (programmaticScrollEndTimerRef.current) {
      window.clearTimeout(programmaticScrollEndTimerRef.current);
      programmaticScrollEndTimerRef.current = null;
    }
    if (positionRetryTimerRef.current) {
      window.clearTimeout(positionRetryTimerRef.current);
      positionRetryTimerRef.current = null;
    }
    if (lateRecalcTimerRef.current) {
      window.clearTimeout(lateRecalcTimerRef.current);
      lateRecalcTimerRef.current = null;
    }
    if (visibilityWatchdogRef.current) {
      window.clearTimeout(visibilityWatchdogRef.current);
      visibilityWatchdogRef.current = null;
    }
    setTutorialStep(0);
    setTooltipVisible(false);
    console.log('🔄 Estado después de cerrar:', { tutorialActive: false, shouldShowTutorial: false });
  };

  const startTutorial = () => {
    console.log('🚀 Iniciando tutorial...');
    console.log('📊 Estado antes de iniciar:', {
      tutorialActive,
      tutorialStep,
      shouldShowTutorial,
      hasAutoStarted: hasAutoStartedTutorial.current
    });
    
    setTutorialActive(true);
    activeStepKeyRef.current = tutorialSteps[0]?.key || '';
    setTutorialStep(0);
    // Forzar la actualización de posición después de un breve delay
    setTimeout(() => {
      console.log('🔄 Forzando actualización de posición...');
      updateTooltipPosition();
      // Retry acotado si aún no visible (mobile)
      let tries = 0;
      const maxTries = 3;
      const interval = setInterval(() => {
        if (tooltipVisibleRef.current || tries >= maxTries || !tutorialActiveRef.current) {
          clearInterval(interval);
          return;
        }
        tries++;
        updateTooltipPosition();
      }, 180);
    }, 200);
  };

  // Función para renderizar el tutorial overlay
  const renderTutorialOverlay = () => {
    console.log('🎯 Renderizando overlay, tutorialActive:', tutorialActive, 'tutorialStep:', tutorialStep);
    if (!tutorialActive) return null;
    
    const step = tutorialSteps[tutorialStep];
    const { top, left, position, arrowOffset } = tooltipPosition;
    const isMobile = window.innerWidth < 768;
    const tooltipWidth = isMobile ? Math.min(280, window.innerWidth - 40) : 320;
    const tooltipHeight = isMobile ? 140 : 120;
    const shouldShow = isMobile ? true : (tooltipVisible || tutorialActiveRef.current);
    
    console.log('📍 Posición del tooltip:', { top, left, position, tooltipVisible });
    
    return (
      <>
        {/* Overlay oscuro */}
        <div className="fixed inset-0 bg-black bg-opacity-50" style={{ zIndex: 2147483645 }} />
        
        {/* Tooltip del tutorial */}
        <div 
          className="fixed rounded-2xl p-4 sm:p-6 shadow-2xl border-2 border-blue-600 transition-all duration-200"
          style={{
            top: `${top}px`,
            left: `${left}px`,
            width: `${tooltipWidth}px`,
            opacity: shouldShow ? 1 : 0,
            transform: shouldShow ? 'scale(1)' : 'scale(0.9)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            zIndex: 2147483647,
            pointerEvents: shouldShow ? 'auto' : 'none',
          }}
        >
          {/* Botón X para cerrar */}
          <button
            onClick={handleTutorialClose}
            className="absolute top-2 sm:top-3 right-2 sm:right-3 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-red-500 text-white flex items-center justify-center text-sm sm:text-lg font-bold hover:bg-red-600 transition-colors"
          >
            ×
          </button>
          
          {/* Contenido del tooltip */}
          <div className="pr-6 sm:pr-8">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
              {step.title}
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
              {step.description}
            </p>
            
            {/* Botones de navegación */}
            <div className="flex justify-between gap-2 sm:gap-3">
              <button
                onClick={handleTutorialClose}
                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-300 transition-colors"
              >
                Saltar
              </button>
              <button
                onClick={handleTutorialNext}
                className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-700 transition-colors"
              >
                {tutorialStep < tutorialSteps.length - 1 ? 'Siguiente' : 'Cerrar'}
              </button>
            </div>
            
            {/* Indicador de progreso */}
            <div className="flex justify-center mt-3 sm:mt-4 space-x-1">
              {tutorialSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === tutorialStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Piquito del tooltip */}
        {(shouldShow && position === 'top') && (
          <>
            {/* Borde del piquito para mejor contraste */}
            <div 
              className="fixed w-0 h-0 border-l-9 border-r-9 border-t-9 border-transparent border-t-blue-800"
              style={{
                top: `${top + tooltipHeight - 1}px`,
                left: `${left + arrowOffset - 9}px`,
                zIndex: 2147483646,
              }}
            />
            {/* Piquito principal */}
            <div 
              className="fixed w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-blue-600"
              style={{
                top: `${top + tooltipHeight}px`,
                left: `${left + arrowOffset - 8}px`,
                zIndex: 2147483647,
              }}
            />
          </>
        )}
        {(shouldShow && position === 'bottom') && (
          <>
            {/* Borde del piquito para mejor contraste */}
            <div 
              className="fixed w-0 h-0 border-l-9 border-r-9 border-b-9 border-transparent border-b-blue-800"
              style={{
                top: `${top - 9}px`,
                left: `${left + arrowOffset - 9}px`,
                zIndex: 2147483646,
              }}
            />
            {/* Piquito principal */}
            <div 
              className="fixed w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-blue-600"
              style={{
                top: `${top - 8}px`,
                left: `${left + arrowOffset - 8}px`,
                zIndex: 2147483647,
              }}
            />
          </>
        )}
      </>
    );
  };

  // Mantener un ref sincronizado del estado de visibilidad para evitar cierres obsoletos
  useEffect(() => {
    tooltipVisibleRef.current = tooltipVisible;
  }, [tooltipVisible]);

  // Mantener ref sincronizado con tutorialActive para evitar cierres obsoletos en timeouts
  useEffect(() => {
    tutorialActiveRef.current = tutorialActive;
  }, [tutorialActive]);

  if (setupLoading) {
    return (
      <div className="flex-1 flex justify-center items-center bg-[#e9ecef]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center bg-[#e9ecef] p-5">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error al cargar</h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">{error}</p>
          <button
            onClick={loadAdminSetup}
            className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#e9ecef] relative">
      {/* Debug Info - Temporal */}
      {/* <DebugInfo /> */}
      
      {/* Renderizar tutorial overlay */}
      {renderTutorialOverlay()}
      
      {/* Header oscuro */}
      <div className="bg-gray-800 pt-10 pb-6 px-5 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-1">¡Bienvenido!</h1>
            <p className="text-lg text-gray-300 font-medium">
              {selectedRestaurantName || 'Panel de Administración'}
            </p>
          </div>
          <div className="w-24 h-20 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center overflow-hidden">
            {selectedRestaurantLogo ? (
              <img 
                src={selectedRestaurantLogo} 
                alt="Logo del restaurante" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl text-white">👨‍💼</span>
            )}
          </div>
        </div>
      </div>

      {/* Header real (fuera del dashboard, en AdminLayout) */}
      {/*
        En AdminLayout, agrega:
        - ref={menuButtonRef} al botón ☰
        - ref={restaurantSelectorRef} al div principal de RestaurantSelector
        (el abrir/cerrar ahora se controla desde el selector, no se requiere ref adicional)
      */}

      <div className="px-3 sm:px-5 pt-5">
        {/* Métricas principales - Solo owner/manager */}
        {isOwnerManager && (
        <div className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
            {/* Métrica de Órdenes */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 flex items-center shadow-md">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gray-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                <span className="text-lg sm:text-2xl">📊</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{statsLoading ? '—' : todayOrders}</div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Órdenes Hoy</div>
              </div>
            </div>
            
            {/* Métrica de Ingresos */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 flex items-center shadow-md">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gray-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                <span className="text-lg sm:text-2xl">💰</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{statsLoading ? '—' : formatCurrency(todaySales)}</div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Ingresos Hoy</div>
              </div>
            </div>
            
            {/* Botón de Ayuda General */}
            <div className="relative flex justify-center sm:justify-start lg:justify-center">
              <button
                className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full border-2 border-blue-600 bg-white shadow-md flex items-center justify-center transition-all duration-200 hover:scale-105 ${
                  helpHover ? 'bg-blue-600 text-white' : 'text-blue-600'
                }`}
                onClick={startTutorial}
                onMouseEnter={() => setHelpHover(true)}
                onMouseLeave={() => setHelpHover(false)}
              >
                {helpHover && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white text-blue-600 px-2 sm:px-3 py-1 rounded-full border border-gray-200 text-xs sm:text-sm font-medium whitespace-nowrap z-10">
                    ¿Cómo funciona?
                  </div>
                )}
                <span className="text-lg sm:text-2xl font-bold animate-pulse">?</span>
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Acciones rápidas y contenido lateral */}
        <div className="mb-9">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Acciones rápidas - 4 por fila */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-3">
                 {quickActions
                  .filter(action => {
                    // Filtrar por roles permitidos usando useAuth
                    // owner/manager/kitchen
                    const role = (user?.role?.id || '').toLowerCase();
                    if (!action.allowedRoles) return true;
                    return action.allowedRoles.includes(role as any);
                  })
                  .map((action, index) => (
                  <div key={index} className="w-full">
                    <div
                      ref={(el) => { quickActionRefs.current[action.title] = el; }}
                      className={`bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.03] hover:shadow-xl ${action.comingSoon ? 'opacity-60 bg-gray-50 border-gray-200' : ''}`}
                      style={{
                        minWidth: 0,
                        width: '100%',
                        aspectRatio: '1 / 1',
                        maxWidth: '100%',
                      }}
                      onClick={() => {
                        if (!action.comingSoon) navigate(action.route);
                      }}
                    >
                      <div className="flex flex-col items-center justify-center w-full h-full p-2 sm:p-3">
                        <div className="mb-1 flex items-center justify-center">
                          <span className={`text-xl sm:text-2xl text-center ${action.comingSoon ? 'opacity-70' : ''}`}>
                            {typeof action.icon === 'string'
                              ? action.icon.startsWith('/')
                                ? <img src={action.icon} alt={action.title} className="w-12 h-12 object-contain sm:w-14 sm:h-14" />
                                : action.icon
                              : action.icon}
                          </span>
                        </div>
                        <span className={`text-xs sm:text-sm font-bold text-gray-800 text-center tracking-wide ${action.comingSoon ? 'opacity-70 text-gray-500' : ''}`}>
                          {action.title}
                        </span>
                        {action.comingSoon && (
                          <div className="bg-amber-500 px-2 py-1 rounded-full mt-1">
                            <span className="text-white text-xs font-semibold text-center">Próximamente</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                 ))}
              </div>
            </div>

            {/* Contenido lateral - Información Útil y Alertas (solo owner/manager) */}
            {isOwnerManager && (
            <div className="lg:col-span-1 space-y-6">
              {/* Alertas */}
              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-md relative">
                <div className="absolute top-3 right-3">
                  <span className="bg-indigo-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    IA
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Consejos de IA</h2>
                  <button
                    type="button"
                    onClick={() => setTipsOpen(v => !v)}
                    className="px-2 py-1 text-sm rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    title={tipsOpen ? 'Colapsar' : 'Expandir'}
                    aria-expanded={tipsOpen ? 'true' : 'false'}
                    aria-controls="ia-tips-panel"
                  >
                    {tipsOpen ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                {tipsOpen && (
                <div className="space-y-3 sm:space-y-4" id="ia-tips-panel">
                  {tipsLoading && (
                    <div className="text-sm text-gray-600">Cargando recomendaciones...</div>
                  )}
                  {!tipsLoading && tipsToday.length === 0 && (
                    <div className="text-sm text-gray-600">Sin recomendaciones para hoy.</div>
                  )}
                  {!tipsLoading && tipsToday.map((tip) => {
                    const prioBadge =
                      tip.priority === 'alto'
                        ? 'bg-red-100 border-red-200 text-red-700'
                        : tip.priority === 'medio'
                          ? 'bg-yellow-100 border-yellow-200 text-yellow-700'
                          : tip.priority === 'bajo'
                            ? 'bg-green-100 border-green-200 text-green-700'
                            : 'bg-gray-100 border-gray-200 text-gray-700';
                    return (
                      <div className="flex items-start" key={tip.id}>
                        <span className="text-xl sm:text-2xl mr-3 mt-1 flex-shrink-0">{tip.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base sm:text-lg font-bold text-blue-800">{tip.title}</h3>
                            <span className={`badge-modern ${prioBadge}`}>Prioridad: {tip.priority !== 'desconocido' ? tip.priority : '—'}</span>
                          </div>
                          {tip.why && (
                            <div className="text-xs sm:text-sm text-gray-700">
                              <span className="font-medium text-gray-800">Por qué: </span>{tip.why}
                            </div>
                          )}
                          {tip.actions && tip.actions.length > 0 ? (
                            <div className="mt-1">
                              <div className="text-[12px] font-medium text-gray-800 mb-1">Acciones sugeridas</div>
                              <ul className="list-disc pl-5 text-xs sm:text-sm text-gray-700 space-y-0.5">
                                {tip.actions.map((a, i) => (
                                  <li key={i}>{a}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm sm:text-base text-blue-700 leading-relaxed">
                              {tip.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
              {/* Plan Actual */}
              <CurrentPlanCard />
            </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
} 